import { useCallback, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PermissionDenied } from '@/components/RequirePermission';
import { useLanguage } from '@/i18n/LanguageContext';
import { newOpId, plainQty } from '@/pages/Exceptions/shared';
import { ScanBannerView, type ScanBanner } from '@/scan/ScanBanner';
import { ScanCapture, type ScanCaptureHandle } from '@/scan/ScanCapture';
import { QtyStepper, qtyToSend } from '@/scan/QtyStepper';
import { errorBanner, ownerChoices, productLabel, type OwnerChoice } from '@/scan/scanModel';
import type { ScanLocation, ScanMoveInput, ScanMoveResult, ScanResolution } from '@/api/types';
import {
  INITIAL_MOVE_STATE, LOG_LIMIT, applyLocation, canScanProducts, changeDest, changeSource, setQuarantine, tallyKey,
  type LocationOutcome, type MoveState, type MoveStep,
} from './moveScanModel';

type LogEntry = { readonly id: number; readonly text: string };

const STEPS: readonly MoveStep[] = ['SOURCE', 'DEST', 'ITEMS'];

/**
 * Move by scanner (Склад -> Перемещение сканером): scan the SOURCE cell, scan the DESTINATION cell, then scan
 * products - every scan moves one unit (or the quantity set) and a repeated scan of the same product raises the
 * count. The state machine is explicit (`moveScanModel`): a cell scanned while products are being scanned changes
 * nothing; the operator presses «Сменить источник» / «Сменить назначение». Quarantine is reachable only through its
 * own switch. One fresh opId per physical scan; the same opId only when a failed request is retried.
 */
export function MoveScanPage(): JSX.Element {
  const { state: auth, hasPermission } = useAuth();
  const { t } = useLanguage();
  const self = auth.status === 'authenticated' ? auth.self : null;
  const fixedWarehouse = self?.warehouse_id ?? null;
  const warehouses = useApi(() => (fixedWarehouse ? Promise.resolve(null) : endpoints.warehousesList()), [fixedWarehouse]);
  const [picked, setPicked] = useState<string>('');

  if (!hasPermission('INVENTORY.WRITE')) return <PermissionDenied reason={t('moveScan.noAccess')} />;
  if (warehouses.status === 'loading') return <LoadingState />;
  if (warehouses.status === 'error') return <ErrorBanner error={warehouses.error} onRetry={warehouses.reload} />;

  const list = warehouses.data?.warehouses ?? [];
  const warehouseId = fixedWarehouse ?? (picked || (list.length === 1 ? list[0]!.id : ''));
  const warehouseName = fixedWarehouse ? (self?.warehouse_name ?? '') : (list.find((w) => w.id === warehouseId)?.name ?? '');

  return (
    <div>
      <div className="page-header">
        <h1>{t('moveScan.title')}</h1>
      </div>
      {fixedWarehouse ? (
        <p className="meta" data-testid="move-warehouse">{t('moveScan.warehouse')}: {warehouseName}</p>
      ) : (
        <div className="field-row">
          <label htmlFor="move-warehouse-select">{t('moveScan.warehouse')}</label>
          <select id="move-warehouse-select" value={warehouseId} onChange={(e) => setPicked(e.target.value)}>
            {list.length !== 1 && <option value="">{t('moveScan.warehouse.choose')}</option>}
            {list.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      )}
      {warehouseId ? <MoveScanSession key={warehouseId} warehouseId={warehouseId} /> : <p className="notice" role="status">{t('moveScan.warehouse.need')}</p>}
    </div>
  );
}

function MoveScanSession({ warehouseId }: { readonly warehouseId: string }): JSX.Element {
  const { t } = useLanguage();
  const [scanOn, setScanOn] = useState(true);
  const [machine, setMachine] = useState<MoveState>(INITIAL_MOVE_STATE);
  const [quarantine, setQuarantineOn] = useState(false);
  const [qty, setQty] = useState('1');
  const [banner, setBanner] = useState<ScanBanner | null>(null);
  const [log, setLog] = useState<readonly LogEntry[]>([]);
  const [pending, setPending] = useState(0);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [owners, setOwners] = useState<{ code: string; qty: string; choices: readonly OwnerChoice[] } | null>(null);

  const capture = useRef<ScanCaptureHandle>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const machineRef = useRef(machine);
  machineRef.current = machine;
  const quarantineRef = useRef(quarantine);
  quarantineRef.current = quarantine;
  const retryRef = useRef<(() => Promise<void>) | null>(null);
  const tally = useRef(new Map<string, number>());
  const logId = useRef(0);

  const enqueue = useCallback((task: () => Promise<void>) => {
    setPending((n) => n + 1);
    queue.current = queue.current.then(task).catch(() => undefined).finally(() => setPending((n) => n - 1));
  }, []);

  function fail(err: unknown, retry: () => Promise<void>): void {
    const b = errorBanner(t, err);
    retryRef.current = b.retryable ? retry : null;
    setBanner(b.retryable ? { ...b, actions: [{ id: 'retry', label: t('scan.retry') }] } : b);
  }

  function locationBanner(outcome: LocationOutcome, loc: ScanLocation): ScanBanner {
    switch (outcome) {
      case 'SOURCE_SET': return { tone: 'ok', title: t('moveScan.sourceSet', { code: loc.code }) };
      case 'DEST_SET': return { tone: 'ok', title: t('moveScan.destSet', { code: loc.code }) };
      case 'RELOCATE_HINT': return { tone: 'info', title: t('moveScan.relocateHint', { code: loc.code }) };
      case 'QUARANTINE_OFF': return { tone: 'warn', title: t('moveScan.quarantineOff', { code: loc.code }) };
      case 'SAME_CELL': return { tone: 'error', title: t('moveScan.sameCell') };
      case 'SOURCE_NOT_ALLOWED': return { tone: 'error', title: t('scan.err.sourceNotAllowed'), detail: loc.code };
      default: return { tone: 'error', title: t('scan.err.destNotAllowed'), detail: loc.code };
    }
  }

  async function move(input: ScanMoveInput, from: string, to: string): Promise<void> {
    try {
      const res: ScanMoveResult = await endpoints.stockScanMove(input);
      retryRef.current = null;
      setOwners(null);
      const added = Number(input.qty ?? '1');
      const key = tallyKey(res.skuId, input.sourceLocationId, input.destinationLocationId);
      const total = (tally.current.get(key) ?? 0) + added;
      tally.current.set(key, total);
      const label = productLabel({ name: res.skuName });
      const text = t('moveScan.moved', { name: label, n: plainQty(String(total)), from, to });
      setBanner({ tone: 'ok', title: text, detail: res.repeatOfRecent ? t('moveScan.repeatHint') : undefined });
      setLog((l) => [{ id: ++logId.current, text: t('moveScan.log.item', { name: label, n: plainQty(String(added)), from, to }) }, ...l].slice(0, LOG_LIMIT));
      setQty('1');
    } catch (err) {
      const choices = ownerChoices(err);
      if (choices) {
        const code = input.productBarcode ?? '';
        setOwners({ code, qty: input.qty ?? '1', choices });
        retryRef.current = null;
        setBanner({
          tone: 'info',
          title: t('scan.pickOwner'),
          detail: code,
          actions: choices.map((c, i) => ({ id: `owner:${i}`, label: `${c.skuName} — ${c.ownerName}` })),
        });
        return;
      }
      fail(err, () => move(input, from, to));
    }
  }

  async function handle(code: string, qtyValue: string, expect?: 'LOCATION' | 'PRODUCT'): Promise<void> {
    let res: ScanResolution;
    try {
      res = await endpoints.scanResolve({ warehouseId, code, ...(expect ? { expect } : {}) });
    } catch (err) {
      fail(err, () => handle(code, qtyValue, expect));
      return;
    }
    retryRef.current = null;
    setOwners(null);
    switch (res.kind) {
      case 'LOCATION': {
        const out = applyLocation(machineRef.current, res.location, quarantineRef.current);
        machineRef.current = out.state;
        setMachine(out.state);
        setBanner(locationBanner(out.outcome, res.location));
        return;
      }
      case 'ARCHIVED_LOCATION':
        setBanner({ tone: 'error', title: t('scan.err.archived'), detail: res.location.code });
        return;
      case 'UNKNOWN':
        setBanner({ tone: 'error', title: t('scan.err.unknown'), detail: code });
        return;
      case 'AMBIGUOUS':
        // A code that is a cell label AND a product barcode: on this screen the current step decides.
        await handle(code, qtyValue, canScanProducts(machineRef.current) ? 'PRODUCT' : 'LOCATION');
        return;
      case 'PRODUCT': {
        const m = machineRef.current;
        if (!canScanProducts(m) || !m.source || !m.dest) {
          setBanner({ tone: 'warn', title: m.step === 'SOURCE' ? t('moveScan.needSource') : t('moveScan.needDest') });
          return;
        }
        const first = res.candidates[0];
        if (first && (first.isSerialTracked || first.isLotTracked)) {
          setBanner({ tone: 'error', title: t('scan.err.tracked') });
          return;
        }
        await move(
          {
            opId: newOpId(), warehouseId, sourceLocationId: m.source.id, destinationLocationId: m.dest.id, productBarcode: code, qty: qtyToSend(qtyValue),
            ...(m.dest.kind === 'QUARANTINE' ? { quarantine: true } : {}),
          },
          m.source.code,
          m.dest.code,
        );
        return;
      }
      default:
    }
  }

  function onScan(code: string): void {
    setLastCode(code);
    const q = qty;
    enqueue(() => handle(code, q));
  }

  function onAction(id: string): void {
    if (id === 'retry') {
      const run = retryRef.current;
      if (run) enqueue(run);
    } else if (id.startsWith('owner:') && owners && machineRef.current.source && machineRef.current.dest) {
      const c = owners.choices[Number(id.slice(6))];
      const m = machineRef.current;
      if (c && m.source && m.dest) {
        const { code, qty: q } = owners;
        const input: ScanMoveInput = {
          opId: newOpId(), warehouseId, sourceLocationId: m.source.id, destinationLocationId: m.dest.id, productBarcode: code, ownerId: c.ownerId, qty: q,
          ...(m.dest.kind === 'QUARANTINE' ? { quarantine: true } : {}),
        };
        setOwners(null);
        enqueue(() => move(input, m.source!.code, m.dest!.code));
      }
    }
  }

  function apply(next: MoveState): void {
    machineRef.current = next;
    setMachine(next);
    setBanner(null);
    capture.current?.focus();
  }

  const stepLabel = (s: MoveStep): string => t(`moveScan.step.${s}` as const);
  const hint = machine.step === 'SOURCE' ? t('moveScan.hint.SOURCE') : machine.step === 'DEST' ? t('moveScan.hint.DEST') : t('moveScan.hint.ITEMS');

  return (
    <div className="move-scan" data-testid="move-scan">
      <ol className="move-steps" aria-label={t('moveScan.steps')}>
        {STEPS.map((s, i) => (
          <li key={s} className={machine.step === s ? 'active' : undefined} aria-current={machine.step === s ? 'step' : undefined} data-testid={`move-step-${s}`}>
            <span className="move-step-n">{i + 1}</span> {stepLabel(s)}
          </li>
        ))}
      </ol>
      <p className="scan-destination" data-testid="move-hint">{hint}</p>

      <div className="move-cells">
        <div className="move-cell" data-testid="move-source">
          <div className="move-cell-label">{t('moveScan.source')}</div>
          <div className="move-cell-value">{machine.source ? machine.source.code : '—'}</div>
          {machine.source && <button type="button" onClick={() => apply(changeSource(machine))}>{t('moveScan.changeSource')}</button>}
        </div>
        <div className="move-cell" data-testid="move-dest">
          <div className="move-cell-label">{t('moveScan.dest')}</div>
          <div className="move-cell-value">{machine.dest ? machine.dest.code : '—'}</div>
          {machine.dest && <button type="button" onClick={() => apply(changeDest(machine))}>{t('moveScan.changeDest')}</button>}
        </div>
      </div>

      <label className="scan-toggle">
        <input
          type="checkbox"
          role="switch"
          checked={quarantine}
          onChange={(e) => {
            setQuarantineOn(e.target.checked);
            quarantineRef.current = e.target.checked;
            apply(setQuarantine(machine, e.target.checked));
          }}
        />
        <span>{t('moveScan.quarantine')}</span>
      </label>
      <p className="meta">{t('moveScan.quarantine.hint')}</p>

      <label className="scan-toggle">
        <input type="checkbox" role="switch" checked={scanOn} onChange={(e) => setScanOn(e.target.checked)} />
        <span>{t('scan.mode')}</span>
      </label>

      <ScanCapture
        ref={capture}
        enabled={scanOn}
        busy={pending > 0}
        lastCode={lastCode}
        onScan={(code) => onScan(code)}
        onDuplicate={() => setBanner({ tone: 'info', title: t('scan.duplicate'), detail: t('scan.duplicate.detail') })}
      />
      <ScanBannerView banner={banner} onAction={onAction} />
      <QtyStepper value={qty} onChange={setQty} onDone={() => capture.current?.focus()} />

      <section className="card" aria-labelledby="move-log-title">
        <h2 id="move-log-title" className="card-title">{t('moveScan.log.title')}</h2>
        {log.length === 0 ? (
          <p className="meta">{t('moveScan.log.empty')}</p>
        ) : (
          <ul className="move-log" data-testid="move-log">
            {log.map((e) => <li key={e.id}>{e.text}</li>)}
          </ul>
        )}
      </section>
    </div>
  );
}
